import React, { useState, useEffect } from 'react';
import { Container, Button, Spinner, Alert } from 'react-bootstrap';
import AdminLayout from '../components/layout/AdminLayout';
import { useCategorias } from '../hooks/useCategorias';
import { supabase } from '../../lib/supabase';
import CategoriasTable from '../components/categorias/CategoriasTable';
import CategoriaModal from '../components/categorias/CategoriaModal';
import CategoriaDeleteConfirm from '../components/categorias/CategoriaDeleteConfirm';

const CategoriasPage = () => {
  const { 
    categorias, 
    loading, 
    error, 
    createCategoria, 
    updateCategoria, 
    deleteCategoria 
  } = useCategorias();

  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedCategoria, setSelectedCategoria] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [productosCount, setProductosCount] = useState({});

  // Cargar contador de productos por categoría
  useEffect(() => {
    const fetchProductosCount = async () => {
      try {
        const { data, error } = await supabase
          .from('productos')
          .select('CodCategoria');

        if (error) throw error;

        // Contar productos por categoría
        const counts = {};
        data.forEach((producto) => {
          counts[producto.CodCategoria] = (counts[producto.CodCategoria] || 0) + 1;
        });

        setProductosCount(counts);
      } catch (err) {
        console.error('Error fetching productos count:', err);
      }
    };

    if (categorias.length > 0) {
      fetchProductosCount();
    }
  }, [categorias]);

  const handleCreate = () => {
    setSelectedCategoria(null);
    setShowModal(true);
  };

  const handleEdit = (categoria) => {
    setSelectedCategoria(categoria);
    setShowModal(true);
  };

  const handleDeleteClick = (categoria) => {
    setSelectedCategoria(categoria);
    setShowDeleteConfirm(true);
  };

  const handleSaveCategoria = async (data) => {
    if (selectedCategoria) {
      await updateCategoria(selectedCategoria.id, data);
    } else {
      await createCategoria(data);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedCategoria) return;

    // Verificar si tiene productos asociados
    const count = productosCount[selectedCategoria.id] || 0;
    if (count > 0) {
      alert(`No se puede eliminar. La categoría tiene ${count} producto(s) asociado(s).`);
      return;
    }

    try {
      setActionLoading(true);
      await deleteCategoria(selectedCategoria.id);
      setShowDeleteConfirm(false);
      setSelectedCategoria(null);
    } catch (err) {
      console.error(err);
      alert('Error al eliminar la categoría');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <AdminLayout>
      <Container fluid className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 style={{ color: '#2A2F35' }} className="m-0 fw-bold">
            <i className="fas fa-tags me-3"></i>
            Gestión de Categorías
          </h2>
          <Button
            onClick={handleCreate}
            style={{ backgroundColor: '#c4956a', borderColor: '#c4956a' }}
            className="fw-semibold"
          >
            <i className="fas fa-plus me-2"></i>Nueva Categoría
          </Button>
        </div>

        <div className="admin-card">
          {error && (
            <Alert variant="danger">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" style={{ color: '#c4956a' }} />
              <p className="mt-3 text-muted mb-0">Cargando categorías...</p>
            </div>
          ) : (
            <CategoriasTable
              categorias={categorias}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              productosCount={productosCount}
            />
          )}
        </div>

        {/* Modal de Crear/Editar */}
        <CategoriaModal
          show={showModal}
          onHide={() => setShowModal(false)}
          onSave={handleSaveCategoria}
          categoriaEdit={selectedCategoria}
          categorias={categorias}
        />

        {/* Modal de Confirmación de Eliminación */}
        <CategoriaDeleteConfirm
          show={showDeleteConfirm}
          onHide={() => setShowDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
          categoria={selectedCategoria}
          loading={actionLoading}
          productosCount={productosCount[selectedCategoria?.id] || 0}
        />
      </Container>
    </AdminLayout>
  );
};

export default CategoriasPage;
